// src/exceptions.ts
var NoSuchElementException = class extends Error {
  constructor(message) {
    super(message);
  }
};

// src/optional.ts
var Optional = class _Optional {
  constructor(value) {
    this.value = value;
  }
  /**
   * Returns an empty Optional instance.
   */
  static empty() {
    return new _Optional();
  }
  /**
   * Returns an Optional with the specified present non-null value.
   *
   * @param value The value to fill the Optional with.
   * @returns  an Optional with the given value.
   */
  static filled(value) {
    return new _Optional(value);
  }
  /**
   * Returns an Optional with the specified present value if non-null, otherwise returns an empty Optional.
   *
   * @param value The value to fill the Optional with.
   * @returns an Optional with the given value if non-null, an empty Optional otherwise.
   */
  static of(value) {
    if (value === void 0 || value === null) return _Optional.empty();
    return new _Optional(value);
  }
  /**
   * Indicates whether some other object is "equal to" this Optional.
   *
   * @param other Object to compare this optional value with.
   * @param comparator The predicate used to compare the values.
   * @returns `true`if the two values are equal, `false` otherwise.
   */
  equals(other, comparator) {
    if (this.isEmpty()) return false;
    if (comparator) return comparator(this.value, other);
    return this.value === other;
  }
  /**
   * Casts this Optional to the given type.
   *
   * @returns a new optional typed as the given type.
   */
  as() {
    return _Optional.of(this.value);
  }
  /**
   * Sets the given value. If a value is already present, this method replaces it.
   *
   * @param value The value to set.
   */
  set(value) {
    this.value = value;
    return this;
  }
  /**
   * If a value is present in this Optional, returns the value, otherwise throws NoSuchElementException.
   *
   * @returns the value if present, throws NoSuchElementException otherwise.
   */
  get() {
    if (this.isEmpty())
      throw new NoSuchElementException(
        "[Optional] You are trying to get an empty value."
      );
    return this.value;
  }
  /**
   * If a value is present, and the value matches the given predicate, return an Optional describing the value, otherwise return an empty Optional.
   *
   * @param predicate To filter the value with if present.
   * @returns a filled Optional if a value is present and it matches the given predicate, and emlpty Optional otherwise.
   */
  filter(predicate) {
    if (this.isEmpty() || !predicate(this.value)) return _Optional.empty();
    return _Optional.of(this.value);
  }
  /**
   * If a value is present, apply the provided mapping function to it, and if the result is non-null, return an Optional describing the result.
   *
   * @param mapper The mapper function.
   * @returns the result Optional if a value is present, an empty Optional otherwise.
   */
  map(mapper) {
    if (this.isEmpty()) return _Optional.empty();
    return _Optional.of(mapper(this.value));
  }
  /**
   * If a value is present, apply the provided Optional-bearing mapping function to it, return that result, otherwise return an empty Optional.
   *
   * @param mapper The mapper function.
   * @returns the result Optional if a value is present, an empty Optional otherwise.
   */
  flatMap(mapper) {
    if (this.isEmpty()) return _Optional.empty();
    return mapper(this.value);
  }
  /**
   * Tests whether this optional is empty.
   *
   * @returns `true` if there is no present value, otherwise `false`.
   */
  isEmpty() {
    if (this.value === void 0 || this.value === null) return true;
    return false;
  }
  /**
   * Tests whether a value is present.
   *
   * @returns `true` if there is a value present, otherwise `false`.
   */
  isPresent() {
    if (this.isEmpty()) return false;
    return true;
  }
  /**
   * If a value is present, invoke the specified consumer with the value, otherwise do nothing.
   *
   * @param consumer The consumer to invoke if a value is present.
   * @returns itself for chaining purposes.
   */
  ifPresent(consumer) {
    if (this.isEmpty()) return this;
    consumer(this.value);
    return this;
  }
  /**
   * Return the value if present, otherwise return other.
   *
   * @param other The default value to return if this Optional is empty.
   * @returns the value if present, otherwise `other`.
   */
  orElse(other) {
    if (this.isEmpty()) return other;
    return this.value;
  }
  /**
   * If the optional is empty, invokes the specified supplier, otherwise do nothing.
   *
   * @param other The default value to return if this Optional is empty.
   * @returns itself for chaining purposes.
   */
  orElseInvoke(procedure) {
    if (this.isPresent()) return this;
    procedure();
    return this;
  }
  /**
   * Return the value if present, otherwise return `undefined`.
   *
   * @returns the value if present, otherwise `undefined`.
   */
  orUndefined() {
    if (this.isEmpty()) return void 0;
    return this.value;
  }
  /**
   * Return the value if present, otherwise return `null`.
   *
   * @returns the value if present, otherwise `null`.
   */
  orNull() {
    if (this.isEmpty()) return null;
    return this.value;
  }
  /**
   * Return the value if present, otherwise invoke other and return the result of that invocation.
   *
   * @param supplier The supplier to invoke when the value is not present.
   * @returns either the value if present, the result of the given supplier otherwise.
   */
  orElseGet(supplier) {
    if (this.isEmpty()) return supplier();
    return this.value;
  }
  /**
   * Return the contained value, if present, otherwise throw an exception to be created by the provided supplier.
   *
   * @param exceptionSupplier
   * @returns
   */
  orElseThrow(exceptionSupplier) {
    if (this.isEmpty()) throw exceptionSupplier();
    return this.value;
  }
  /**
   * If a value is present, apply the provided resolver function to it, and if the result is non-null, return an Optional describing the result.
   *
   * @param resolver The resolver function.
   * @param other The value to return if the Optional is empty.
   * @returns the result Optional if a value is present, the given `other` parameter otherwise.
   */
  resolve(resolver, other) {
    if (this.isEmpty()) return other;
    return resolver(this.value);
  }
};

// src/strings.ts
var EMPTY = "";
var FORWARD_SLASH = "/";
var BACKSLASH = "\\";
var HASHTAG = "#";
var SPACE = " ";
var TAB = "	";
var NEW_LINE = "\n";
var UNDERSCORE = "_";
var MINUS = "-";
var PLUS = "+";
var DOT = ".";
var COLON = ":";
var WILDCARD = "*";
var PERCENTAGE = "%";
var COMMA = ",";
var QUESTION_MARK = "?";
var UNIT_NORMAL_METRIC = "Nm";
var UNIT_MASS = "g/m\xB2";
var UNIT_WEIGHT = "g";
var THREE_DOTS = "\u2026";
var STRING_ONLY_ALPHA_CHARS_REGEX = /^[a-z]+$/i;
var STRING_ONLY_NUMERIC_CHARS_REGEX = /^[1-9]+$/i;
var STRING_EMAIL_VALIDATION_REGEXP = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
var SPECIALS_TO_PLAIN_CHARS = /* @__PURE__ */ new Map([
  ["\xE1", "a"],
  ["\xE0", "a"],
  ["\xE2", "a"],
  ["\xE6", "a"],
  ["\xAA", "a"],
  ["\xE4", "a"],
  ["\xE3", "a"],
  ["\xE5", "a"],
  ["\u0101", "a"],
  ["\xE7", "c"],
  ["\u0107", "c"],
  ["\u010D", "c"],
  ["\xE9", "e"],
  ["\xE8", "e"],
  ["\xEA", "e"],
  ["\xEB", "e"],
  ["\u0119", "e"],
  ["\u0117", "e"],
  ["\u0113", "e"],
  ["\xEE", "i"],
  ["\xEF", "i"],
  ["\xEC", "i"],
  ["\xED", "i"],
  ["\u012F", "i"],
  ["\u012B", "i"],
  ["\xF1", "n"],
  ["\u0144", "n"],
  ["\xF4", "o"],
  ["\u0153", "o"],
  ["\xBA", "o"],
  ["\xF6", "o"],
  ["\xF2", "o"],
  ["\xF3", "o"],
  ["\xF5", "o"],
  ["\xF8", "o"],
  ["\xFB", "u"],
  ["\xF9", "u"],
  ["\xFC", "u"],
  ["\xFA", "u"],
  ["\u016B", "u"],
  ["\xFF", "y"],
  ["\u2026", "..."]
]);
var stringPlainify = (value) => {
  const result = [];
  for (let i = 0; i < value.length; i++) {
    const char = value.charAt(i);
    const plainChar = SPECIALS_TO_PLAIN_CHARS.get(char);
    result.push(plainChar ? plainChar : char);
  }
  return result.join(EMPTY);
};
var stringIsAlpha = (value) => {
  return value.match(STRING_ONLY_ALPHA_CHARS_REGEX) !== null;
};
var stringIsNumeric = (value) => {
  return value.match(STRING_ONLY_NUMERIC_CHARS_REGEX) !== null;
};
var stringGetNaturalComparator = (order = "desc") => (a, b) => {
  if (order === "asc") {
    return stringPlainify(a.toLowerCase()).localeCompare(stringPlainify(b.toLowerCase()));
  }
  return stringPlainify(b.toLowerCase()).localeCompare(stringPlainify(a.toLowerCase()));
};
var stringGetNaturalValueComparator = (order = "desc", extractor) => (a, b) => {
  const stringA = extractor(a);
  const stringB = extractor(b);
  if (order === "asc") {
    return stringPlainify(stringA.toLowerCase()).localeCompare(stringPlainify(stringB.toLowerCase()));
  }
  return stringPlainify(stringB.toLowerCase()).localeCompare(stringPlainify(stringA.toLowerCase()));
};
var STRING_NATURAL_COMPARATOR_ASC = stringGetNaturalComparator("asc");
var STRING_NATURAL_COMPARATOR_DESC = stringGetNaturalComparator("desc");
var STRING_NATURAL_VALUE_COMPARATOR_ASC = (extractor) => stringGetNaturalValueComparator("asc", extractor);
var STRING_NATURAL_VALUE_COMPARATOR_DESC = (extractor) => stringGetNaturalValueComparator("desc", extractor);
var stringIsEmpty = (value) => {
  return value === EMPTY;
};
var stringIsBlank = (value) => {
  return value === void 0 || value === null || stringIsEmpty(value);
};
var stringIsFilled = (value) => {
  return !stringIsBlank(value);
};
var stringCamelCaseToSnakeCase = (text) => {
  return text.split(/(?=[A-Z])/).join("_").toLowerCase();
};
var stringCamelCaseToKebabCase = (text) => {
  return text.split(/(?=[A-Z])/).join("-").toLowerCase();
};
var stringSnakeCaseToCamelCase = (text) => {
  return text.replace(/(?!^)_(.)/g, (_, char) => char.toUpperCase());
};
var stringKebabCaseToCamelCase = (text) => {
  return text.replace(/-./g, (x) => x[1].toUpperCase());
};
var stringIsString = (data) => {
  return typeof data === "string";
};
var stringHumanize = (value) => {
  const words = value.match(/[A-Za-z][a-z]*|[0-9]+/g) || [];
  return words.map((word) => word.toLowerCase()).join(" ");
};
var stringCamelize = (value) => {
  return value.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
    return index === 0 ? word.toLowerCase() : word.toUpperCase();
  }).replace(/\s+/g, "");
};
var stringParseBoolean = (input) => {
  if (!input) return;
  try {
    return JSON.parse(input.toLowerCase());
  } catch (e) {
    return;
  }
};
var stringPad = (value, nbOfCharacters, paddingCharacter = EMPTY) => {
  const val = value;
  if (Math.abs(nbOfCharacters) <= val.length) {
    return val;
  }
  const m = Math.max(Math.abs(nbOfCharacters) - value.length || 0, 0);
  const pad = Array(m + 1).join(String(paddingCharacter).charAt(0));
  return nbOfCharacters < 0 ? pad + val : val + pad;
};
var stringEquals = (value1, value2) => {
  return value1.trim() === value2?.trim();
};
var stringValueOrEmpty = (value) => {
  if (value === false || stringIsBlank(value)) return EMPTY;
  return value;
};
var stringExtractEmailDomain = (email) => {
  const results = email.toLowerCase().match(/.*@(.*)\..*/);
  if (results?.length === 2) return results[1];
};
var stringCapitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};
var stringCapitalizeEachWord = (string) => {
  const eachWord = string.split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  return eachWord.join(" ");
};
var StringHelpers = {
  /**
   * Plainify the given string, converting every special character into its plain brother.
   *
   * @param value The string to convert.
   * @returns a plainified string.
   */
  plainify: stringPlainify,
  /**
   * Tests whether the given data parameter is a string.
   *
   * @param data The data to test.
   * @returns `true` if the given data is a string, `false` otherwise.
   */
  isString: stringIsString,
  /**
   * Converts the given snake case formatted string into a camel case format.
   *
   * @param text The string to convert.
   * @returns the converted string result.
   */
  snakeCaseToCamelCase: stringSnakeCaseToCamelCase,
  /**
   * Converts the given camel case formatted string into a kebab case format.
   *
   * @param text The string to convert.
   * @returns the converted string result.
   */
  camelCaseToKebabCase: stringCamelCaseToKebabCase,
  /**
   * Converts the given kebab case formatted string into a camel case format.
   *
   * @param text The string to convert.
   * @returns the converted string result.
   */
  kebabCaseToCamelCase: stringKebabCaseToCamelCase,
  /**
   * Converts the given camel case formatted string into a snake case format.
   *
   * @param text The string to convert.
   * @returns the converted string result.
   */
  camelCaseToSnakeCase: stringCamelCaseToSnakeCase,
  /**
   * Humanize the given string.
   *
   * @param value the string to humanize.
   * @returns the formatted result.
   */
  humanize: stringHumanize,
  /**
   * Camelize the given string.
   *
   * @param value the string to camelize.
   * @returns the formatted result.
   */
  camelize: stringCamelize,
  /**
   * Tests whether the given string contains only alpha characters.
   *
   * @param value The string to check.
   * @returns `true` if the given string only contains alpha characters, `false` otherwise.
   */
  isAlpha: stringIsAlpha,
  /**
   * Tests whether the given string contains only numeric characters.
   *
   * @param value The string to check.
   * @returns `true` if the given string only contains numeric characters, `false` otherwise.
   */
  isNumeric: stringIsNumeric,
  /**
   * Test whether the two given string trimed value are equal.
   * @param value1 The first value to compare.
   * @param value2 The second value to compare.
   * @returns `true` if the two values are equal once trimed, `false` otherwise.
   */
  equals: stringEquals,
  /**
   * Converted any given blank values to an empty string (""). If the given value contains a string, the method will return it unarmed.
   *
   * @param value The value to convert.
   * @returns the exact given string if not blank, an empty string otherwise.
   */
  valueOrEmpty: stringValueOrEmpty,
  /**
   * Formats the given string value to contain at least the given number of characters,
   * adding the given padding character at the end of the value if needed.
   *
   * @param value the value to format.
   * @param nbOfCharacters The desired minimum number of characters.
   * @param paddingCharacter The padding character.
   * @returns the formatted result.
   */
  pad: stringPad,
  /**
   * Test whether the given string is strictly equal to an empty string.
   *
   * @param value The string to test.
   * @returns `true` if the given string is empty, `false` otherwise.
   */
  isEmpty: stringIsEmpty,
  /**
   * Test whether the given string is either `undefined`, `null` or equal to an empty string.
   *
   * @param value The string to test.
   * @returns `true` if the given string is blank, `false` otherwise.
   */
  isBlank: stringIsBlank,
  /**
   * Test whether the given string is neither `undefined`, `null` nor equal to an empty string.
   *
   * @param value The string to test.
   * @returns `true` if the given string is filled, `false` otherwise.
   */
  isFilled: stringIsFilled,
  /**
   * Extract the domain name from the given email address.
   *
   * @param email The email address.
   * @returns the corresponding domain name.
   */
  extractEmailDomain: stringExtractEmailDomain,
  /**
   * Capitalizes the first letter of the given string.
   *
   * @param string the string to compute.
   * @returns the result string.
   */
  capitalize: stringCapitalize,
  /**
   * Capitalizes the first letter of each word of the given string.
   *
   * @param string the string to compute.
   * @returns the result string.
   */
  capitalizeEachWord: stringCapitalizeEachWord
};
var StringComparators = {
  /**
   * An ascendent natural string comparator.
   */
  naturalAsc: STRING_NATURAL_COMPARATOR_ASC,
  /**
   * An descendent natural string comparator.
   */
  naturalDesc: STRING_NATURAL_COMPARATOR_DESC,
  /**
   * An ascendent natural string comparator from extracted values.
   */
  naturalValueAsc: STRING_NATURAL_VALUE_COMPARATOR_ASC,
  /**
   * An descendent natural string comparator from extracted values.
   */
  naturalValueDesc: STRING_NATURAL_VALUE_COMPARATOR_DESC
};
var StringParsers = {
  /**
   * Extract a boolean value from the given string.
   *
   * @param input The boolean-string to parse.
   * @returns the boolean value corresponding to the given string. If the given input is `null`, this methid resturns `undefined`.
   */
  parseBoolean: stringParseBoolean
};
var StringRegexs = {
  /**
   * Regex pattern that matches string containing only alpha characters.
   */
  alpha: STRING_ONLY_ALPHA_CHARS_REGEX,
  /**
   * Regex pattern that matches string containing only numeric characters.
   */
  numeric: STRING_ONLY_NUMERIC_CHARS_REGEX,
  /**
   * Regex pattern that matches an email addess.
   */
  email: STRING_EMAIL_VALIDATION_REGEXP
};
var StringSymbols = {
  EMPTY,
  FORWARD_SLASH,
  HASHTAG,
  SPACE,
  UNDERSCORE,
  MINUS,
  PLUS,
  DOT,
  COLON,
  WILDCARD,
  PERCENTAGE,
  COMMA,
  QUESTION_MARK,
  UNIT_NORMAL_METRIC,
  UNIT_MASS,
  UNIT_WEIGHT,
  THREE_DOTS
};
var Strings = {
  /**
   * String symbols.
   */
  symbol: StringSymbols,
  /**
   * String helper methods.
   */
  helper: StringHelpers,
  /**
   * String comparators.
   */
  comparator: StringComparators,
  /**
   * String parsers.
   */
  parser: StringParsers,
  /**
   * String regexs.
   */
  regex: StringRegexs
};

// src/arrays.ts
var arrayMatchOneOrMore = (array, predicate) => {
  return array.filter((value) => !!value).filter(predicate).length > 0;
};
var arrayMatchOneExactly = (array, predicate) => {
  return array.filter((value) => !!value).filter(predicate).length === 1;
};
var arrayMatchAll = (array, predicate) => {
  const cleanedArray = array.filter((value) => !!value);
  return cleanedArray.length > 0 && cleanedArray.filter(predicate).length === cleanedArray.length;
};
var arraysAreSame = (array1, array2, comparator) => {
  return array1.length == array2.length && array1.every(
    (element, index) => comparator !== void 0 ? comparator(element, array2[index]) : element === array2[index]
  );
};
var arrayCreateSuite = (length, offset = 1, step = 1) => {
  return Array.from({ length }, (_, i) => offset + i * step);
};
var EMPTY_ARRAY = [];
var arrayCleanStringArray = (array) => {
  if (!array) return;
  return array.filter((value) => !stringIsBlank(value));
};
var arrayShuffle = (array) => {
  let currentIndex = array.length;
  let temporaryValue, randomIndex;
  while (currentIndex) {
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }
  return array;
};
var arrayUniq = (array) => {
  return array.filter((v, i, a) => a.indexOf(v) === i);
};
var arrayUniqObjectsByProperty = (array, property, comparator) => {
  return array.filter(
    (obj, i) => array.findIndex(
      (a) => comparator ? comparator(a[property], obj[property]) : a[property] === obj[property]
    ) === i
  );
};
var arrayLast = (array) => {
  const length = array.length;
  if (length === 0) return;
  return array[array.length - 1];
};
var arrayLastOptional = (array) => {
  const length = array.length;
  if (length === 0) return Optional.empty();
  return Optional.filled(array[array.length - 1]);
};
var arrayFirst = (array) => {
  if (array.length === 0) return;
  return array[0];
};
var arraySum = (array, valueExtractor) => {
  if (array.length === 0) return 0;
  return array.filter((v) => v !== null && v !== void 0).map((value) => valueExtractor ? valueExtractor(value) : Number(value)).reduce((total, current) => total + current, 0);
};
var arrayAverage = (array, valueExtractor, rounded = true) => {
  if (array.length === 0) return 0;
  const total = arraySum(array, valueExtractor);
  const average = total / array.length;
  const finalAverage = rounded ? Math.round(average) : average;
  return finalAverage >= 0 ? finalAverage : 0;
};
var arrayFirstOptional = (array) => {
  if (array.length === 0) return Optional.empty();
  return Optional.filled(array[0]);
};
var arrayPushNewValue = (array, newValue, valueComparator) => {
  if (array.length === 0) {
    array.push(newValue);
    return;
  }
  if (!array.find(
    (value) => valueComparator ? valueComparator(value, newValue) : value === newValue
  )) {
    array.push(newValue);
  }
};
var arrayPushNewValues = (array, newValues, valueComparator) => {
  if (array.length === 0) {
    array.push(...newValues);
    return;
  }
  for (const newValue of newValues) {
    arrayPushNewValue(array, newValue, valueComparator);
  }
};
var ArrayHelpers = {
  /**
   * Tests if all elements of the given array match the predicate.
   *
   * @param array The array to test
   * @param predicate The predicate used to test each element of the given array
   * @returns `true` if all of the elements match the given predicate, `false` otherwise.
   */
  matchAll: arrayMatchAll,
  /**
   * Tests if exactly one element of the given array matches the predicate.
   *
   * @param array The array to test
   * @param predicate The predicate used to test each element of the given array
   * @returns `true` if one element only matches the given predicate, `false` otherwise.
   */
  matchOneExactly: arrayMatchOneExactly,
  /**
   * Tests if at least one element of the given array matches the predicate.
   *
   * @param array The array to test
   * @param predicate The predicate used to test each element of the given array
   * @returns `true` if at leat one element matches the given predicate, `false` otherwise.
   */
  matchOneOrMore: arrayMatchOneOrMore,
  /**
   * Tests whether the given arrays are exactly the same in length and content.
   * If no predicate is given to compare the elements of the arrays, `===` will be used.
   *
   * @param array1 The first array to test
   * @param array2 The second array to test
   * @param comparator The predicate used to compare each element of the given arrays.
   * @returns `true` if both arrays are equal, `false` otherwise.
   */
  same: arraysAreSame,
  /**
   * Strips the given string array of any blank values (either `null`, `undefined` of empty string).
   *
   * @param array The string array to be cleaned.
   * @returns `undefined` if the given array was undefined, the cleaned up array otherwise.
   */
  cleanStringArray: arrayCleanStringArray,
  /**
   * Creates an array of the given length composed of a suite of numbers from `offset` to `offset + (length * step)`.
   *
   * @param length The length of the array to be created.
   * @param offset The starting offset of the suite to be created. `1` is set by default.
   * @returns the suite array from `offset` to `offset + (length * step)`.
   */
  createSuite: arrayCreateSuite,
  /**
   * Shuffles the elements of the given array to a random order.
   *
   * @param array The array to shuffle.
   * @returns An array with every elements of the given array but in a random order.
   */
  shuffle: arrayShuffle,
  /**
   * Strips the given array of duplicate values.
   *
   * @param array The array with potential duplicates.
   * @returns An array where of elements are uniq.
   */
  uniq: arrayUniq,
  /**
   * Strips the given array of duplicate property values.
   *
   * @param array The array with potential duplicates.
   * @param property The property to extract for the comparaison.
   * @param comparator The predicate used to compare each element of the given arrays.
   * @returns An array where of elements are uniq.
   */
  uniqObjectsByProperty: arrayUniqObjectsByProperty,
  /**
   * Extracts the last element from the given array.
   *
   * @param array The array to extract the last element from.
   * @returns the last element.
   */
  last: arrayLast,
  /**
   * Extracts the last element from the given array as an optional.
   *
   * @param array The array to extract the last element from.
   * @returns the last element as an optional.
   */
  lastOptional: arrayLastOptional,
  /**
   * Extracts the first element from the given array.
   *
   * @param array The array to extract the first element from.
   * @returns the first element.
   */
  first: arrayFirst,
  /**
   * Extracts the first element from the given array as an optional.
   *
   * @param array The array to extract the first element from.
   * @returns the first element as an optional.
   */
  firstOptional: arrayFirstOptional,
  /**
   * Calculate the sum of the extracted values.
   *
   * @param array The array.
   * @param valueExtractor The extractor used to retrieve values to be sum up.
   * @returns the total.
   */
  sum: arraySum,
  /**
   * Calculate the average of the extracted values.
   *
   * @param array The array.
   * @param valueExtractor The extractor used to retrieve values to be averaged up.
   * @returns the average.
   */
  average: arrayAverage,
  /**
   * Push the given value to the given array only if the value is not already present in the given array.
   *
   * @param array The array in which to insert the new value.
   * @param newValue The new value to insert.
   * @param valueComparator The comparator used to compare values from the array.
   */
  pushNewValue: arrayPushNewValue,
  /**
   * Push the given values to the given array only if the values are not already present in the given array.
   * The duplicate values will not be pushed but any other values will be.
   *
   * @param array The array in which to insert the new value.
   * @param newValue The new values to insert.
   * @param valueComparator The comparator used to compare values from the array.
   */
  pushNewValues: arrayPushNewValues
};
var ArraySymbols = {
  /**
   * An empty array.
   */
  empty: EMPTY_ARRAY
};
var Arrays = {
  /**
   * Array helper methods.
   */
  helper: ArrayHelpers,
  /**
   * Array symbols.
   */
  symbol: ArraySymbols
};

// src/dates.ts
import moment from "moment";
var DATE_DEFAULT_SEPARATOR = FORWARD_SLASH;
var DATE_TO_FORMATED = {
  ["short-date-fr"]: (moment2, separator) => moment2.format(`DD${separator}MM${separator}YYYY`),
  ["short-date-us"]: (moment2, separator) => moment2.format(`MM${separator}DD${separator}YYYY`),
  ["short-date-time-fr"]: (moment2, separator) => moment2.format(`DD${separator}MM${separator}YYYY HH:mm`),
  ["short-date-time-us"]: (moment2, separator) => moment2.format(`MM${separator}DD${separator}YYYY HH:mm`),
  ["basic-date"]: (moment2, separator) => moment2.format(`YYYY${separator}MMdd`),
  ["basic-date-time"]: (moment2, separator) => moment2.format(`YYYY${separator}MM${separator}DDTHH:mm:ss.SSSZ`),
  ["reverse"]: (moment2, separator) => moment2.format(`YYYY${separator}MM${separator}DD`)
};
var dateFormat = (date, options) => {
  const { separator = DATE_DEFAULT_SEPARATOR, format = "short-date-fr" } = options ?? {};
  if (!date) {
    return "-";
  }
  return DATE_TO_FORMATED[format](moment(date), separator);
};
var dateTodayAsString = (options) => {
  const now = new Date(Date.now());
  return dateFormat(now, options);
};
var dateGetComparator = (order = "desc") => (a, b) => {
  const aTime = a.getTime();
  const bTime = b.getTime();
  if (order === "asc") return aTime - bTime;
  return bTime - aTime;
};
var DATE_COMPARATOR_ASC = dateGetComparator("asc");
var DATE_COMPARATOR_DESC = dateGetComparator("desc");
var dateGetAsStringComparator = (dateFormat2, order = "desc") => (a, b) => {
  const date1 = moment(a, dateFormat2).toDate();
  const date2 = moment(b, dateFormat2).toDate();
  return dateGetComparator(order)(date1, date2);
};
var DATE_AS_STRING_COMPARATOR_ASC = dateGetAsStringComparator("asc");
var DATE_AS_STRING_COMPARATOR_DESC = dateGetAsStringComparator("desc");
var dateNow = () => moment(Date.now()).toDate();
var dateDaysAgo = (nbOfDays = 1) => {
  const today = dateNow();
  today.setDate(today.getDate() - nbOfDays);
  return today;
};
var dateInDays = (nbOfDays = 1) => {
  const today = dateNow();
  today.setDate(today.getDate() + nbOfDays);
  return today;
};
var dateYesterday = () => dateDaysAgo();
var dateTomorrow = () => dateInDays();
var dateIsBefore = (date1, date2) => date1 < date2;
var dateIsAfter = (date1, date2) => date1 > date2;
var dateIsSameDay = (date1, date2) => {
  return date1.getDay() === date2.getDay() && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear();
};
var dateIsBeforeInDays = (date, nbOfDays = 1) => dateIsBefore(date, dateInDays(nbOfDays));
var dateIsAfterInDays = (date, nbOfDays = 1) => dateIsAfter(date, dateInDays(nbOfDays));
var dateIsBeforeDaysAgo = (date, nbOfDays = 1) => dateIsBefore(date, dateDaysAgo(nbOfDays));
var dateIsAfterDaysAgo = (date, nbOfDays = 1) => dateIsAfter(date, dateDaysAgo(nbOfDays));
var dateIsToday = (date) => dateIsSameDay(date, dateNow());
var dateIsPast = (date) => !dateIsToday(date) && dateIsBefore(date, dateNow());
var dateIsFuture = (date) => !dateIsToday(date) && dateIsAfter(date, dateNow());
var dateIsTomorrow = (date) => dateIsSameDay(date, dateTomorrow());
var dateIsYesterday = (date) => dateIsSameDay(date, dateYesterday());
var dateIsDaysAgo = (date, nbOfDays = 1) => dateIsSameDay(date, dateDaysAgo(nbOfDays));
var dateIsInDays = (date, nbOfDays = 1) => dateIsSameDay(date, dateInDays(nbOfDays));
var DateHelpers = {
  /**
   * Formats a date.
   *
   * @param date The date to format.
   * @param options Options.
   * @returns The given date formated accordingly to the given options.
   */
  format: dateFormat,
  /**
   * Gets today's date as a string formated in the format specified in the given options parameter.
   *
   * @param separator The separator between day, month and year.
   * @returns today's date as a string.
   */
  today: dateTodayAsString,
  /**
   * Gets a Date corresponding to now.
   *
   * @returns a `Date` object.
   */
  now: dateNow,
  /**
   * Gets a Date corresponding to the given number of days in the past.
   *
   * @param nbOfDays The number of days in the past.
   * @returns a `Date` object.
   */
  daysAgo: dateDaysAgo,
  /**
   * Gets a Date corresponding to the given number of days in the future.
   *
   * @param nbOfDays The number of days in the future.
   * @returns a `Date` object.
   */
  inDays: dateInDays,
  /**
   * Gets a Date corresponding to yesterday.
   *
   * @returns a `Date` object.
   */
  yesterday: dateYesterday,
  /**
   * Gets a Date corresponding to yesterday.
   *
   * @returns a `Date` object.
   */
  tomorrow: dateTomorrow,
  /**
   * Tests whether the first given date is before the second given date.
   *
   * @param date The first date to compare.
   * @param date The second date to compare.
   * @returns `true` if the first given date is before the second given date, `false` otherwise.
   */
  isBefore: dateIsBefore,
  /**
   * Tests whether the first given date is after the second given date.
   *
   * @param date The first date to compare.
   * @param date The second date to compare.
   * @returns `true` if the first given date is after the second given date, `false` otherwise.
   */
  isAfter: dateIsAfter,
  /**
   * Tests whether the first given date is the same as the second given date.
   *
   * @param date The first date to compare.
   * @param date The second date to compare.
   * @returns `true` if the first given date is the same as the second given date, `false` otherwise.
   */
  isSameDay: dateIsSameDay,
  /**
   * Tests whether the given date happened before the given number of days in the future relatively to today.
   *
   * @param date The date to compare.
   * @param nbOfDays The number of days in the future.
   * @returns `true` if the given date happened before `today + nbOfDays`, `false` otherwise.
   */
  isBeforeInDays: dateIsBeforeInDays,
  /**
   * Tests whether the given date happens after the given number of days in the future relatively to today.
   *
   * @param date The date to compare.
   * @param nbOfDays The number of days in the future.
   * @returns `true` if the given date happens after `today + nbOfDays`, `false` otherwise.
   */
  isAfterInDays: dateIsAfterInDays,
  /**
   * Tests whether the given date happened before the given number of days in the past relatively to today.
   *
   * @param date The date to compare.
   * @param nbOfDays The number of days in the future.
   * @returns `true` if the given date happened before `today - nbOfDays`, `false` otherwise.
   */
  isBeforeDaysAgo: dateIsBeforeDaysAgo,
  /**
   * Tests whether the given date happens after the given number of days in the past relatively to today.
   *
   * @param date The date to compare.
   * @param nbOfDays The number of days in the future.
   * @returns `true` if the given date happens after `today - nbOfDays`, `false` otherwise.
   */
  isAfterDaysAgo: dateIsAfterDaysAgo,
  /**
   * Tests whether the given date happens today.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happens today, `false` otherwise.
   */
  isToday: dateIsToday,
  /**
   * Tests whether the given date happened before today.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happened before today, `false` otherwise.
   */
  isPast: dateIsPast,
  /**
   * Tests whether the given date happens after today.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happens after today, `false` otherwise.
   */
  isFuture: dateIsFuture,
  /**
   * Tests whether the given date happens tomorrow.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happens tomorrow, `false` otherwise.
   */
  isTomorrow: dateIsTomorrow,
  /**
   * Tests whether the given date happened yesterday.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happened yesterday, `false` otherwise.
   */
  isYesterday: dateIsYesterday,
  /**
   * Tests whether the given date happened the given number of days in the past.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happened `today - nbOfDays`, `false` otherwise.
   */
  isDaysAgo: dateIsDaysAgo,
  /**
   * Tests whether the given date happens the given number of days in the future.
   *
   * @param date The date to compare.
   * @returns `true` if the given date happens `today + nbOfDays`, `false` otherwise.
   */
  isInDays: dateIsInDays
};
var DateComparators = {
  /**
   * An ascendent date comparator.
   */
  asc: DATE_COMPARATOR_ASC,
  /**
   * An descendent date comparator.
   */
  desc: DATE_COMPARATOR_DESC,
  /**
   * An ascendent formated date comparator.
   */
  asStringAsc: DATE_AS_STRING_COMPARATOR_ASC,
  /**
   * An descendent formated date comparator.
   */
  asStringDesc: DATE_AS_STRING_COMPARATOR_DESC
};
var Dates = {
  /**
   * Date helper methods.
   */
  helper: DateHelpers,
  /**
   * Date comparators.
   */
  comparator: DateComparators
};

// src/functions.ts
var functionIdentity = () => (value) => value;
var functionIdentityAs = () => (value) => value;
var functionAttribute = (attributeName) => {
  return (value) => {
    const propertyDescriptor = Object.getOwnPropertyDescriptor(
      value,
      attributeName
    );
    if (propertyDescriptor !== void 0 && propertyDescriptor.value !== null) {
      return propertyDescriptor.value;
    }
  };
};
var functionVoid = () => () => {
};
var FunctionHelpers = {
  /**
   * A function that returns the exact given value.
   *
   * @param value The value parameter of the function.
   * @returns a function that returns the exact given value.
   */
  identity: functionIdentity,
  /**
   * A function that returns the given value casted to the given type.
   *
   * @param value The value parameter of the function.
   * @returns a function that returns the given value casted to the given type.
   */
  identityAs: functionIdentityAs,
  /**
   * A function that extracts an attribute from the given value.
   *
   * @param attributeName The name of the attribute to extract.
   * @returns a function that extract the attrobute from the given object.
   */
  attribute: functionAttribute,
  /**
   * A void function.
   *
   * @returns a void function.
   */
  void: functionVoid
};
var Functions = {
  /**
   * Function helper methods.
   */
  helper: FunctionHelpers
};

// src/indexSignatures.ts
var indexSignatureFromArray = (array, idExtractor) => {
  const result = {};
  for (const element of array) {
    result[idExtractor(element)] = element;
  }
  return result;
};
var indexSignatureFromArrayValues = (array, idExtractor, valueExtractor) => {
  const result = {};
  for (const element of array) {
    result[idExtractor(element)] = valueExtractor(element);
  }
  return result;
};
var indexSignatureMapToArray = (indexSignature, mapper, filter) => {
  const result = [];
  for (const [id, element] of Object.entries(indexSignature).filter(
    ([id2, element2]) => !filter || filter(id2, element2)
  )) {
    result.push(mapper(id, element));
  }
  return result;
};
var indexSignatureMapValues = (indexSignature, mapper, filter) => {
  const result = {};
  for (const [id, element] of Object.entries(indexSignature).filter(
    ([, element2]) => !filter || filter(element2)
  )) {
    result[id] = mapper(element);
  }
  return result;
};
var indexSignaturesAreSame = (indexSignature1, indexSignature2) => {
  if (!arraysAreSame(Object.keys(indexSignature1), Object.keys(indexSignature2)))
    return false;
  if (!arraysAreSame(
    Object.values(indexSignature1),
    Object.values(indexSignature2)
  ))
    return false;
  return true;
};
var indexSignatureIncludes = (indexSignature, indexSignatures) => {
  for (const iSig of indexSignatures) {
    if (indexSignaturesAreSame(iSig, indexSignature)) {
      return true;
    }
  }
  return false;
};
var indexSignatureToString = (indexSignature) => {
  const resultArray = [];
  for (const [id, element] of Object.entries(indexSignature)) {
    const value = stringIsString(element) ? `'${element}'` : `${element}`;
    resultArray.push(`${id}: ${value}`);
  }
  return `{ ${resultArray.join(", ")} }`;
};
var indexSignatureToArray = (indexSignature) => {
  const result = [];
  for (const element of Object.values(indexSignature)) result.push(element);
  return result;
};
var indexSignatureEntriesToArray = (indexSignature, mapper) => {
  const result = [];
  for (const [id, element] of Object.entries(indexSignature))
    result.push(mapper(id, element));
  return result;
};
var indexSignatureKeysToArray = (indexSignature) => {
  const result = [];
  for (const element of Object.keys(indexSignature)) result.push(element);
  return result;
};
var indexSignatureFilter = (indexSignature, filter) => {
  const result = {};
  for (const [id, element] of Object.entries(indexSignature)) {
    if (filter(id, element)) {
      result[id] = element;
    }
  }
  return result;
};
var indexSignatureGroupBy = (array, indexExtractor) => {
  const result = {};
  for (const value of array) {
    const index = indexExtractor(value);
    if (result[index] === void 0) result[index] = [];
    result[index].push(value);
  }
  return result;
};
var indexSignatureGroupByAndMap = (array, indexExtractor, mapper) => {
  const result = {};
  for (const value of array) {
    const index = indexExtractor(value);
    if (result[index] === void 0) result[index] = [];
    result[index].push(mapper(value));
  }
  return result;
};
var IndexSignatureHelpers = {
  /**
   * Creates an index signature from the given array.
   *
   * @param array The array to convert.
   * @param idExtractor A function to extract the id from each array elements.
   * @returns the created index signature.
   */
  fromArray: indexSignatureFromArray,
  /**
   * Creates an index signature from the given array with every elements mapped using the given valueExtractor.
   *
   * @param array The array to convert.
   * @param idExtractor A function to extract the id from each array elements.
   * @param valueExtractor A mapper function to convert each element of the array from T to U.
   * @returns the created index signature.
   */
  fromArrayValues: indexSignatureFromArrayValues,
  /**
   * Apply the provided mapping function to all values of the given index signature, and extract all values into an array.
   *
   * @param indexSignature The index signature to map.
   * @param mapper The mapper function.
   * @param filter (optional) A filter predicate used to filter out unwanted values.
   * @returns the result array of mapped values.
   */
  mapToArray: indexSignatureMapToArray,
  /**
   * Apply the provided mapping function to all values of the given index signature, and extract all values into an array.
   *
   * @param indexSignature The index signature to map.
   * @param mapper The mapper function.
   * @param filter (optional) A filter predicate used to filter out unwanted values.
   * @returns the result array of mapped values.
   */
  mapValues: indexSignatureMapValues,
  /**
   * Tests whether the two given index signatures are equals.
   *
   * @param indexSignature1 The first index signature to compare.
   * @param indexSignature2 The second index signature to compare.
   * @returns `true` if the index signatures are equal, `false` otherwise.
   */
  same: indexSignaturesAreSame,
  /**
   * Tests whether the given index signature is included into the given array of index signatures.
   *
   * @param indexSignature The index signature to compare.
   * @param indexSignature2 An array of index signatures to test against the first parameter.
   * @returns `true` if the given index signatures is included into the array, `false` otherwise.
   */
  includes: indexSignatureIncludes,
  /**
   * Converts the given index signature into a string formated as follow:
   *
   * ```
   *    { key1: value1, key2: value2, key3: value3, ...}
   * ```
   *
   * @param indexSignature The index signature to convert.
   * @returns a string representation of the given index signature.
   */
  toString: indexSignatureToString,
  /**
   * Converts an index signature's values into an array.
   *
   * @param indexSignature The index signature to convert.
   * @returns an array of all values of the given index signature.
   */
  toArray: indexSignatureToArray,
  /**
   * Converts an index signature' keys and values converted by applying the given mapping function into an array.
   *
   * @param indexSignature The index signature to convert.
   * @param mapper The mapper function.
   * @returns an array corresponding to all entries of the given index signature using the given mapper function.
   */
  entriesToArray: indexSignatureEntriesToArray,
  /**
   * Converts an index signature's keys into an array.
   *
   * @param indexSignature The index signature to convert.
   * @returns an array of all keys of the given index signature.
   */
  keysToArray: indexSignatureKeysToArray,
  /**
   * Filters out all entries from the given index signature that doesn't match the given predicate.
   *
   * @param indexSignature The index signture to filter.
   * @param filter Predicate to filter the entries.
   * @returns an index signature containing only the element that match the given predicate.
   */
  filter: indexSignatureFilter,
  /**
   * Groups all elements of the given array by the key extracted using the given indexExtractor.
   *
   * @param array The array to convert.
   * @param indexExtractor The extractor function to extract a key from each element.
   * @returns an index signature with arrays of values grouped by keys.
   */
  groupBy: indexSignatureGroupBy,
  /**
   * Apply the given mapping function to every elements and groups all elements of the given array by the key extracted using the given indexExtractor
   *
   * @param array The array to convert.
   * @param indexExtractor The extractor function to extract a key from each element.
   * @param mapper The mapper function.
   * @returns an index signature with arrays of converted values grouped by keys.
   */
  groupByAndMap: indexSignatureGroupByAndMap
};
var IndexSignatures = {
  /**
   * Index signatures helper methods.
   */
  helper: IndexSignatureHelpers
};

// src/jsons.ts
var jsonFlatten = /* @__PURE__ */ function(isArray, wrapped) {
  return function(table) {
    return reduce("", {}, table);
  };
  function reduce(path, accumulator, table) {
    if (isArray(table)) {
      const length = table.length;
      if (length) {
        let index = 0;
        while (index < length) {
          const property = path + "[" + index + "]", item = table[index++];
          if (wrapped(item) !== item) accumulator[property] = item;
          else reduce(property, accumulator, item);
        }
      } else accumulator[path] = table;
    } else {
      let empty = true;
      if (path) {
        for (let property in table) {
          const item = table[property];
          property = path + "." + property;
          empty = false;
          if (wrapped(item) !== item) accumulator[property] = item;
          else reduce(property, accumulator, item);
        }
      } else {
        for (const property in table) {
          const item = table[property];
          empty = false;
          if (wrapped(item) !== item) accumulator[property] = item;
          else reduce(property, accumulator, item);
        }
      }
      if (empty) accumulator[path] = table;
    }
    return accumulator;
  }
}(Array.isArray, Object);
var JsonHelpers = {
  /**
   * Flattens the given JSON object.
   *
   * For example the following JSON structure:
   *
   * ```
   *  {
   *    "key1": {
   *      "key2": "value1"
   *    },
   *    "key3": [
   *      {
   *        "key4": "value2",
   *      },
   *      {
   *        "key5": "value3",
   *      }
   *    ]
   *  }
   * ```
   *
   * Will be transformed into:
   *
   * ```
   *  {
   *    "key1.key2": "value1",
   *    "key3[0].key4": "value2",
   *    "key3[1].key5": "value3"
   *  }
   * ```
   *
   * @param JsonObject The JSON object to flatten.
   * @returns the flattened version of the goven JSON object.
   */
  flatten: jsonFlatten
};
var Jsons = {
  /**
   * JSON helper methods.
   */
  helper: JsonHelpers
};

// src/numbers.ts
var numberAverage = (numbers, rounded = false) => {
  if (numbers.length === 0) return 0;
  const total = numbers.filter((v) => v !== null).reduce((total2, current) => total2 + current, 0);
  const average = total / numbers.length;
  const finalAverage = rounded ? Math.round(average) : average;
  return finalAverage >= 0 ? finalAverage : 0;
};
var numberGetComparator = (order = "desc") => (a, b) => {
  if (order === "asc") return a - b;
  return b - a;
};
var numberGetValueComparator = (order = "desc", extractor) => (a, b) => {
  const numberA = extractor(a);
  const numberB = extractor(b);
  if (order === "asc") return numberA - numberB;
  return numberB - numberA;
};
var NUMBER_COMPARATOR_ASC = numberGetComparator("asc");
var NUMBER_COMPARATOR_DESC = numberGetComparator("desc");
var NUMBER_VALUE_COMPARATOR_ASC = (extractor) => numberGetValueComparator("asc", extractor);
var NUMBER_VALUE_COMPARATOR_DESC = (extractor) => numberGetValueComparator("desc", extractor);
var numberRandom = (min, max) => {
  return Math.random() * (max - min) + min;
};
var numberFormatPercentage = (percentage, options) => {
  const { nbOfDecimals = 1, withNegativeValues = false, forceDisplaySign = false } = options ?? {};
  const fixedPercentage = Number(percentage.toFixed(nbOfDecimals));
  if (Number.isNaN(fixedPercentage)) return "0%";
  if (!withNegativeValues && fixedPercentage <= 0) return "0%";
  if (fixedPercentage >= 100) return `${stringValueOrEmpty(forceDisplaySign && "+")}100%`;
  if (Number.isInteger(fixedPercentage)) return `${numberFormatInteger(Math.round(percentage), options)}%`;
  return `${numberFormatFloatWithDecimals(fixedPercentage, options)}%`;
};
var numberFormatFloatWithDecimals = (float, options) => {
  const { separator = DOT, nbOfDecimals = 1 } = options ?? {};
  if (!float || Number.isNaN(float)) return `0${separator}0`;
  return numberFormatLarge(float, { ...options, nbOfDecimals });
};
var numberFormatFloat = (float, options) => {
  const { nbOfDecimals = 1 } = options ?? {};
  if (!float || Number.isNaN(float)) return "0";
  const fixed = Number(float.toFixed(nbOfDecimals));
  if (Number.isNaN(fixed)) return "0";
  if (Number.isInteger(fixed)) return `${numberFormatInteger(float, options)}`;
  return `${numberFormatLarge(fixed, { ...options, nbOfDecimals })}`;
};
var numberFormatInteger = (integer, options) => {
  const { minDigits = 1, forceDisplaySign = false } = options ?? {};
  const rounded = Math.round(integer);
  if (minDigits > 1) {
    return `${stringValueOrEmpty(forceDisplaySign && rounded > 0 && "+")}${String(rounded).padStart(minDigits, "0")}`;
  }
  return `${numberFormatLarge(rounded, options)}`;
};
var numberFormatLarge = (value, options) => {
  const {
    nbOfDecimals = Number.isInteger(value) ? 0 : 1,
    separator = DOT,
    thousandSeparator = SPACE,
    forceDisplaySign = false
  } = options ?? {};
  return `${stringValueOrEmpty(forceDisplaySign && value > 0 && "+")}${value.toFixed(nbOfDecimals).replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, thousandSeparator).replace(DOT, separator)}`;
};
var NumberHelpers = {
  /**
   * Calculates the average of all numbers given in the array.
   *
   * @param numbers The numbers to be averaged.
   * @param rounded whether the result average should be rounded.
   * @returns the average result.
   */
  average: numberAverage,
  /**
   * Returns a random number contained between the given mion and max values inclusively.
   *
   * @param min The min value.
   * @param max The max value.
   * @returns the result random number.
   */
  random: numberRandom
};
var NumberComparators = {
  /**
   * An ascendent number comparator.
   */
  asc: NUMBER_COMPARATOR_ASC,
  /**
   * An descendent number comparator.
   */
  desc: NUMBER_COMPARATOR_DESC,
  /**
   * An ascendent number comparator from extracted values.
   */
  valueAsc: NUMBER_VALUE_COMPARATOR_ASC,
  /**
   * An descendent number comparator from extracted values.
   */
  valueDesc: NUMBER_VALUE_COMPARATOR_DESC
};
var NumberFormatters = {
  /**
   * Formats the given number into a percentage string representation.
   *
   * @param percentage The number to format.
   * @param options The formatter options.
   * @returns a percentage string representation of the given number.
   */
  percentage: numberFormatPercentage,
  /**
   * Formats the given number into a float string representation with forced decimal.
   *
   * @param percentage The number to format.
   * @param options The formatter options.
   * @returns a float string representation of the given number.
   */
  floatWithDecimal: numberFormatFloatWithDecimals,
  /**
   * Formats the given number into a float string representation. No decimal will be displayed of not needed.
   *
   * @param percentage The number to format.
   * @param options The formatter options.
   * @returns either a float or integer string representation of the given number.
   */
  float: numberFormatFloat,
  /**
   * Formats the given number into a integer string representation.
   *
   * @param percentage The number to format.
   * @param options The formatter options.
   * @returns either a float or integer string representation of the given number.
   */
  integer: numberFormatInteger,
  /**
   * Formats the given number into a string representation handling thousand separators.
   *
   * @param value The number to format.
   * @param options The formatter options.
   * @returns the string representation of the given number.
   */
  large: numberFormatLarge
};
var Numbers = {
  /**
   * Number helper methods.
   */
  helper: NumberHelpers,
  /**
   * Number comparators.
   */
  comparator: NumberComparators,
  /**
   * Number formatters.
   */
  formatter: NumberFormatters
};

// src/reflexions.ts
var reflectionListStaticAttributesAs = (classType, filter) => {
  const objectPropertyNames = Object.getOwnPropertyNames(class {
  });
  const names = Object.getOwnPropertyNames(classType).filter((name) => {
    if (objectPropertyNames.includes(name)) return false;
    return filter ? filter(name) : true;
  });
  return names.map((name) => Object.getOwnPropertyDescriptor(classType, name)).filter((descriptor) => descriptor !== void 0).map(
    (descriptor) => descriptor !== void 0 ? descriptor.value : void 0
  );
};
var ReflexionHelpers = {
  /**
   * Lists all static attributes of a given class.
   *
   * @param classType The type of the class.
   * @param filter A predicate used to filter out unwanted methods or attributes.
   * @returns an array of result values.
   */
  listStaticAttributesAs: reflectionListStaticAttributesAs
};
var Reflexions = {
  /**
   * Reflexion helper methods.
   */
  helper: ReflexionHelpers
};

// src/styles.ts
import chroma from "chroma-js";
var styleEm = (valueInPx) => {
  return `${valueInPx / 16}em`;
};
var styleRem = (valueInPx) => {
  return `${valueInPx / 16}rem`;
};
function styleIsColorDark(red, green, blue) {
  const [r, g, b] = [red, green, blue].map((c) => c / 255).map((c) => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance <= 0.179;
}
function styleIsValidHex(hex) {
  return /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(hex);
}
var styleTransparentize = (color, alpha) => chroma(color).alpha(alpha).css();
var inOriginalFormat = (originalColor, newColor) => {
  return styleIsValidHex(originalColor) ? newColor.hex() : newColor.css();
};
var styleTint = (color, ratio) => {
  const tint = chroma.mix(color, "#fff", ratio, "rgb");
  return inOriginalFormat(color, tint);
};
var styleShade = (color, ratio) => {
  const shade = chroma.mix(color, "#000", ratio, "rgb");
  return inOriginalFormat(color, shade);
};
var styleSaturate = (color, amount) => {
  const saturate = chroma(color).set("hsl.s", `+${amount}`);
  return inOriginalFormat(color, saturate);
};
var styleDesaturate = (color, amount) => {
  const desaturate = chroma(color).set("hsl.s", `-${amount}`);
  return inOriginalFormat(color, desaturate);
};
var styleLightness = (color) => chroma(color).get("hsl.l") * 100;
var styleDarken = (color, amount) => chroma(color).darken(amount).hex();
var styleBrighten = (color, amount) => chroma(color).brighten(amount).hex();
var asHex = (value) => {
  const hex = parseInt(value, 10).toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};
var styleRgbToHex = (rgb) => {
  const withoutWhitespace = rgb.replace(/\s+/g, "");
  const rgbMatch = withoutWhitespace.match(/^rgba?\((\d+),(\d+),(\d+)(?:,(?:1(?:\.0*)?|0(?:\.\d+)?))?\)$/i);
  if (!rgbMatch) {
    return "";
  }
  const [, r, g, b] = rgbMatch;
  return `#${asHex(r)}${asHex(g)}${asHex(b)}`;
};
function styleRgbToHsv({ r, g, b }) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let hue;
  const value = max;
  const saturation = max === 0 ? 0 : delta / max;
  switch (max) {
    case min:
    default:
      hue = 0;
      break;
    case r:
      hue = (g - b) / delta + (g < b ? 6 : 0);
      break;
    case g:
      hue = (b - r) / delta + 2;
      break;
    case b:
      hue = (r - g) / delta + 4;
      break;
  }
  return {
    h: hue * 60,
    s: saturation,
    v: value
  };
}
var styleHexToRgb = (hex) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (_, r1, g1, b1) => r1 + r1 + g1 + g1 + b1 + b1);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const [, r, g, b] = result;
    return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
  }
  return [0, 0, 0];
};
var styleHexToHsv = (hex) => {
  const [r, g, b] = styleHexToRgb(hex);
  return styleRgbToHsv({ r, g, b });
};
var styleHsvToRgb = ({ h, s, v }) => {
  h /= 60;
  const fn = (n) => {
    const k = (n + h) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const r = fn(5);
  const g = fn(3);
  const b = fn(1);
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
};
var styleHsvToHex = ({ h, s, v }) => {
  const { r, g, b } = styleHsvToRgb({ h, s, v });
  return styleRgbToHex(`rgb(${r}, ${g}, ${b})`);
};
function styleCalculateLuminance(r, g, b) {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}
function styleCalculateContrast(rgb1, rgb2) {
  let contrast = (styleCalculateLuminance(rgb1[0], rgb1[1], rgb1[2]) + 0.05) / (styleCalculateLuminance(rgb2[0], rgb2[1], rgb2[2]) + 0.05);
  if (contrast < 1) {
    contrast = 1 / contrast;
  }
  return contrast;
}
var StyleHelpers = {
  /**
   * Converts the given number of pixel into Em values and format it into a string.
   *
   * @param valueInPx The value in pixel to convert.
   * @returns the string representation of the equivalent in Em of the given pixel amount.
   */
  em: styleEm,
  /**
   * Converts the given number of pixel into Rem values and format it into a string.
   *
   * @param valueInPx The value in pixel to convert.
   * @returns the string representation of the equivalent in Rem of the given pixel amount.
   */
  rem: styleRem,
  /**
   * This function calculates if the specified color is "dark", which usually means
   * you need light text if you use it as a background color to fulfill WCAG contrast
   * requirement.
   * The color must be specified via its red, green and blue value in the range of
   * 0 to 255.
   * The formula is based on this Stackoverflow answer: https://stackoverflow.com/a/3943023
   * which itself is based upon the WCAG recommendation for color contrast.
   *
   * @param {number} red The red component in the range 0 to 255
   * @param {number} green The green component in the range 0 to 255
   * @param {number} blue The blue component in the range 0 to 255
   * @returns {boolean} True if the color is dark, false otherwise.
   */
  isColorDark: styleIsColorDark,
  /**
   * Checks whether the given value is a valid hexadecimal color.
   *
   * @param hex The value to test.
   * @returns `true` if the value is a valid hexadecimal color, `false`otherwise.
   */
  isValidHex: styleIsValidHex,
  /**
   * Makes a color more transparent.
   *
   * @param color - Color to manipulate
   * @param alpha - alpha channel value. From 0-1.
   */
  transparentize: styleTransparentize,
  /**
   * Mixes a provided color with white.
   *
   * @param color - Color to mix with white
   * @param ratio - Mix weight. From 0-1. Larger value indicates more white.
   */
  tint: styleTint,
  /**
   * Mixes a provided color with black.
   *
   * @param color - Color to mix with black
   * @param ratio - Mix weight. From 0-1. Larger value indicates more black.
   */
  shade: styleShade,
  /**
   * Increases the saturation of a color by manipulating the hsl saturation.
   *
   * @param color - Color to manipulate
   * @param amount - Amount to change in absolute terms. 0-1.
   */
  saturate: styleSaturate,
  /**
   * Decreases the saturation of a color by manipulating the hsl saturation.
   *
   * @param color - Color to manipulate
   * @param amount - Amount to change in absolute terms. 0-1.
   */
  desaturate: styleDesaturate,
  /**
   * Returns the lightness value of a color. 0-100
   *
   * @param color
   */
  lightness: styleLightness,
  /**
   * Returns the darken value of a color. 0-100
   * @param color - Color to manipulate
   * @param amount - Amount to change in absolute terms. 0-1.
   */
  darken: styleDarken,
  /**
   * Returns the brighten value of a color. 0-100
   *
   * @param color - Color to manipulate
   * @param amount - Amount to change in absolute terms. 0-1.
   */
  brighten: styleBrighten,
  /**
   * Converts a RGB color into a hexadecimal color.
   *
   * @param rgb The value to convert.
   * @returns the result.
   */
  rgbToHex: styleRgbToHex,
  /**
   * Converts a RGB color into a HSV color.
   *
   * @param rgb The value to convert.
   * @returns the result.
   */
  rgbToHsv: styleRgbToHsv,
  /**
   * Converts a hexadecimal color into a RGB color.
   *
   * @param hex The value to convert.
   * @returns the result.
   */
  hexToRgb: styleHexToRgb,
  /**
   * Converts a hexadecimal color into a HSV color.
   *
   * @param hex The value to convert.
   * @returns the result.
   */
  hexToHsv: styleHexToHsv,
  /**
   * Converts a HSV color into a RGB color.
   *
   * @param hex The value to convert.
   * @returns the result.
   */
  hsvToRgb: styleHsvToRgb,
  /**
   * Converts a HSV color into a hexadecimal color.
   *
   * @param hex The value to convert.
   * @returns the result.
   */
  hsvToHex: styleHsvToHex,
  /**
   * Calculates the luminance of the given RGB values.
   *
   * @param r The red value.
   * @param g The green value.
   * @param b The blue value.
   * @returns the luminance.
   */
  calculateLuminance: styleCalculateLuminance,
  /**
   * Calculates the contract between the 2 given RGB values.
   *
   * @param rgb1 The first value.
   * @param rgb2 The second value.
   * @returns the contrast.
   */
  calculateContrast: styleCalculateContrast
};
var Styles = {
  /**
   * Style helper methods.
   */
  helper: StyleHelpers
};

// src/trees.ts
var treePush = (tree, at, children, comparator) => {
  if (comparator(tree, at) === 0) {
    tree.children = children;
    return;
  }
  if (!tree.children) return;
  for (const child of tree.children) {
    treePush(child, at, children, comparator);
  }
};
var treeDoGenerateFromArray = (from, tree, array, comparator) => {
  const children = array.filter(
    (element) => element.parent ? comparator(from, element.parent) === 0 : false
  );
  treePush(tree, from, children, comparator);
  for (const child of children) {
    treeDoGenerateFromArray(child, tree, array, comparator);
  }
};
var treeFromArray = (array, comparator) => {
  const root = array.find(({ parent }) => !parent);
  if (!root) return;
  const tree = root;
  treeDoGenerateFromArray(root, tree, array, comparator);
  return tree;
};
var treeTruncate = (tree, from, comparator) => {
  if (comparator(tree, from) === 0) {
    return tree;
  }
  if (tree.children) {
    for (const child of tree.children) {
      const match = treeTruncate(child, from, comparator);
      if (match) return match;
    }
  }
};
var treeDoGenerateArray = (node, array, comparator) => {
  array.push(node);
  if (!node.parent) return;
  treeDoGenerateArray(node.parent, array, comparator);
};
var treeGetAncestorsTrail = (node, comparator) => {
  const array = [];
  treeDoGenerateArray(node, array, comparator);
  return array.reverse();
};
var TreeHelpers = {
  /**
   * Adds a new elements into the tree at the given node.
   *
   * @param tree The tree to add the new element into.
   * @param at The node in the tree where to add the new element.
   * @param children The element to add.
   * @param comparator The tree node comparator.
   */
  push: treePush,
  /**
   * Creates a tree structure from the given array.
   *
   * @param array The array to create the tree from.
   * @param comparator The tree nodes comparator.
   * @returns the result tree.
   */
  fromArray: treeFromArray,
  /**
   * Truncates the given tree from the given node.
   *
   * @param tree The tree to truncate.
   * @param from The node to truncate the tree from.
   * @param comparator The tree nodes comparator.
   * @returns the truncated tree.
   */
  truncate: treeTruncate,
  /**
   * Gets an array containing all the ancestors of the given node in an ascending order.
   *
   * @param node The node from which to extract the ancestor trail.
   * @param comparator The tree nodes comparator.
   * @returns an array containing all the ancestors of the given node in an ascending order.
   */
  getAncestorsTrail: treeGetAncestorsTrail
};
var Trees = {
  /**
   * Tree helper methods.
   */
  helper: TreeHelpers
};

// src/cryptos.ts
var cryptoUIDGenerate = () => {
  const firstPart = Math.random() * 46656 | 0;
  const secondPart = Math.random() * 46656 | 0;
  const stringFirstPart = ("000" + firstPart.toString(36)).slice(-3);
  const stringSecondPart = ("000" + secondPart.toString(36)).slice(-3);
  return stringFirstPart + stringSecondPart;
};
var cryptoHashString = async (value, algorythm = "SHA-256") => {
  const uint8Array = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest(algorythm, uint8Array);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hash;
};
var CryptoHelpers = {
  /**
   * Generates a UID.
   *
   * @returns the generated UID.
   */
  generateUID: cryptoUIDGenerate,
  hashString: cryptoHashString
};
var Cryptos = {
  /**
   * UID helper methods.
   */
  helper: CryptoHelpers
};

// src/types.ts
var TypeError = class extends Error {
};
var typeIsBrand = (value, brand) => {
  return value[__brand] === brand;
};
var typeCreateBranded = (value, validator) => {
  if (validator && !validator(value)) {
    throw new TypeError(`TypeError: [${value}] doesn't match the desired brand.`);
  }
  return value;
};
var typeCreateBrandedArray = (values, validator) => {
  return values.map((value) => typeCreateBranded(value, validator));
};
var typeIsEmailAddress = (email) => {
  return !!email.toLowerCase().match(STRING_EMAIL_VALIDATION_REGEXP);
};
var typeAssertEmailAddress = (email) => {
  if (!email.toLowerCase().match(STRING_EMAIL_VALIDATION_REGEXP)) {
    throw new Error(`TypeError: [${email}] is not an email address`);
  }
};
var typeIsCallback = (maybeFunc) => {
  return typeof maybeFunc === "function";
};
var TypeHelpers = {
  /**
   * Checks whether the given value corresponds to the given brand name.
   *
   * @param value The value to test.
   * @param brand The name of the expected branded type.
   * @returns `true` if the given value corresponds to the given brand name, `false` otherwise.
   */
  isBrand: typeIsBrand,
  /**
   * Creates a `Branded` type from the given value.
   *
   * @param value The value from which to create the `Branded` type.
   * @param validator (optional) A custom validator to validate the given value.
   * @returns The `Branded` type.
   */
  createBranded: typeCreateBranded,
  /**
   * Creates an array of `Branded` types from the given values.
   *
   * @param values The values from which to create the `Branded` types.
   * @param validator (optional) A custom validator to validate the given values.
   * @returns The `Branded` types.
   */
  createBrandedArray: typeCreateBrandedArray,
  /**
   * Checks whether the given string is an email address.
   *
   * @param email the potential email string.
   * @returns `true` if the given string is an email address, `false` otherwise.
   */
  isEmailAddress: typeIsEmailAddress,
  /**
   * Asserts that the given string is an email address.
   *
   * @param email the potential email string.
   * @throws `TypeError` if the gievn string is not a valid email address.
   */
  assertEmailAddress: typeAssertEmailAddress,
  /**
   * Checks whether the given parameter is callable.
   *
   * @param maybeFunc The parameter to test.
   * @returns `true` is the given parameter is callable, `false` otherwise.
   */
  isCallback: typeIsCallback
};
var Types = {
  /**
   * Type helper methods.
   */
  helper: TypeHelpers
};
export {
  ArrayHelpers,
  ArraySymbols,
  Arrays,
  BACKSLASH,
  COLON,
  COMMA,
  CryptoHelpers,
  Cryptos,
  DATE_AS_STRING_COMPARATOR_ASC,
  DATE_AS_STRING_COMPARATOR_DESC,
  DATE_COMPARATOR_ASC,
  DATE_COMPARATOR_DESC,
  DATE_DEFAULT_SEPARATOR,
  DOT,
  DateComparators,
  DateHelpers,
  Dates,
  EMPTY,
  EMPTY_ARRAY,
  FORWARD_SLASH,
  FunctionHelpers,
  Functions,
  HASHTAG,
  IndexSignatureHelpers,
  IndexSignatures,
  JsonHelpers,
  Jsons,
  MINUS,
  NEW_LINE,
  NUMBER_COMPARATOR_ASC,
  NUMBER_COMPARATOR_DESC,
  NUMBER_VALUE_COMPARATOR_ASC,
  NUMBER_VALUE_COMPARATOR_DESC,
  NumberComparators,
  NumberFormatters,
  NumberHelpers,
  Numbers,
  Optional,
  PERCENTAGE,
  PLUS,
  QUESTION_MARK,
  ReflexionHelpers,
  Reflexions,
  SPACE,
  STRING_EMAIL_VALIDATION_REGEXP,
  STRING_NATURAL_COMPARATOR_ASC,
  STRING_NATURAL_COMPARATOR_DESC,
  STRING_NATURAL_VALUE_COMPARATOR_ASC,
  STRING_NATURAL_VALUE_COMPARATOR_DESC,
  STRING_ONLY_ALPHA_CHARS_REGEX,
  STRING_ONLY_NUMERIC_CHARS_REGEX,
  StringComparators,
  StringHelpers,
  StringParsers,
  StringRegexs,
  StringSymbols,
  Strings,
  StyleHelpers,
  Styles,
  TAB,
  THREE_DOTS,
  TreeHelpers,
  Trees,
  TypeError,
  TypeHelpers,
  Types,
  UNDERSCORE,
  UNIT_MASS,
  UNIT_NORMAL_METRIC,
  UNIT_WEIGHT,
  WILDCARD,
  arrayAverage,
  arrayCleanStringArray,
  arrayCreateSuite,
  arrayFirst,
  arrayFirstOptional,
  arrayLast,
  arrayLastOptional,
  arrayMatchAll,
  arrayMatchOneExactly,
  arrayMatchOneOrMore,
  arrayPushNewValue,
  arrayPushNewValues,
  arrayShuffle,
  arraySum,
  arrayUniq,
  arrayUniqObjectsByProperty,
  arraysAreSame,
  cryptoHashString,
  cryptoUIDGenerate,
  dateDaysAgo,
  dateFormat,
  dateGetAsStringComparator,
  dateGetComparator,
  dateInDays,
  dateIsAfter,
  dateIsAfterDaysAgo,
  dateIsAfterInDays,
  dateIsBefore,
  dateIsBeforeDaysAgo,
  dateIsBeforeInDays,
  dateIsDaysAgo,
  dateIsFuture,
  dateIsInDays,
  dateIsPast,
  dateIsSameDay,
  dateIsToday,
  dateIsTomorrow,
  dateIsYesterday,
  dateNow,
  dateTodayAsString,
  dateTomorrow,
  dateYesterday,
  functionAttribute,
  functionIdentity,
  functionIdentityAs,
  functionVoid,
  indexSignatureEntriesToArray,
  indexSignatureFilter,
  indexSignatureFromArray,
  indexSignatureFromArrayValues,
  indexSignatureGroupBy,
  indexSignatureGroupByAndMap,
  indexSignatureIncludes,
  indexSignatureKeysToArray,
  indexSignatureMapToArray,
  indexSignatureMapValues,
  indexSignatureToArray,
  indexSignatureToString,
  indexSignaturesAreSame,
  jsonFlatten,
  numberAverage,
  numberFormatFloat,
  numberFormatFloatWithDecimals,
  numberFormatInteger,
  numberFormatLarge,
  numberFormatPercentage,
  numberGetComparator,
  numberGetValueComparator,
  numberRandom,
  reflectionListStaticAttributesAs,
  stringCamelCaseToKebabCase,
  stringCamelCaseToSnakeCase,
  stringCamelize,
  stringCapitalize,
  stringCapitalizeEachWord,
  stringEquals,
  stringExtractEmailDomain,
  stringGetNaturalComparator,
  stringGetNaturalValueComparator,
  stringHumanize,
  stringIsAlpha,
  stringIsBlank,
  stringIsEmpty,
  stringIsFilled,
  stringIsNumeric,
  stringIsString,
  stringKebabCaseToCamelCase,
  stringPad,
  stringParseBoolean,
  stringPlainify,
  stringSnakeCaseToCamelCase,
  stringValueOrEmpty,
  styleBrighten,
  styleCalculateContrast,
  styleCalculateLuminance,
  styleDarken,
  styleDesaturate,
  styleEm,
  styleHexToHsv,
  styleHexToRgb,
  styleHsvToHex,
  styleHsvToRgb,
  styleIsColorDark,
  styleIsValidHex,
  styleLightness,
  styleRem,
  styleRgbToHex,
  styleRgbToHsv,
  styleSaturate,
  styleShade,
  styleTint,
  styleTransparentize,
  treeFromArray,
  treeGetAncestorsTrail,
  treePush,
  treeTruncate,
  typeAssertEmailAddress,
  typeCreateBranded,
  typeCreateBrandedArray,
  typeIsBrand,
  typeIsCallback,
  typeIsEmailAddress
};
